import { useEffect, useState } from 'react';

/** Subscribes to a media query. SSR/no-matchMedia safe. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The handheld breakpoint, as a media query (ADR 0048).
 *
 * A custom property cannot appear in a media query, so the value is written out
 * here and in every `@media (width < 60rem)` block. It is `--breakpoint-handheld`
 * in tokens.css, and `useMediaQuery.test.ts` fails if the two ever disagree —
 * the old `useStackedLayout()` said 75rem while the CSS said 60rem, and the
 * next caller would have inherited that split.
 */
export const HANDHELD_QUERY = '(width < 60rem)';

/**
 * The primary pointer is a mouse or trackpad. What drag-and-drop of message
 * rows waits for (#168): a touch screen's long-press belongs to selection.
 */
export const FINE_POINTER_QUERY = '(pointer: fine)';

/**
 * True below 60rem: one pane per screen, the phone shell, the document scrolls.
 *
 * `AppShell` mirrors this onto `.shell[data-layout='handheld']`, so CSS can key
 * on the shell it is inside rather than repeat the query.
 */
export function useHandheld(): boolean {
  return useMediaQuery(HANDHELD_QUERY);
}
